import { Loader, Upload, X } from "lucide-react";
import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { HexColorPicker } from "react-colorful";
import Toast from "./Toast";

const SOCIAL_LINK_KEYS = ["facebook", "instagram", "linkedin", "website", "twitter"];

const normalizeSocialLinks = (blog) => {
    return SOCIAL_LINK_KEYS.reduce((acc, key) => {
        const value = blog?.socialLinks?.[key] ?? blog?.[key] ?? "";
        if (typeof value === "string") {
            acc[key] = value;
        } else if (value != null) {
            acc[key] = String(value);
        } else {
            acc[key] = "";
        }
        return acc;
    }, {});
};

const BlogModal = ({
    blog,
    onClose,
    onSaved,
    addAndEditLoader,
    setAddAndEditLoader
}) => {
    const [title, setTitle] = useState("");
    const [subTitle, setSubTitle] = useState("");
    const [category, setCategory] = useState("");
    const [titlePosition, setTitlePosition] = useState("bottom");
    const [shortDescription, setShortDescription] = useState("");
    const [description, setDescription] = useState("");
    const [titleColor, setTitleColor] = useState("#000000");
    const [categoryColor, setCategoryColor] = useState("#000000");
    const [showTitleColorPicker, setShowTitleColorPicker] = useState(false);
    const [showCategoryColorPicker, setShowCategoryColorPicker] = useState(false);
    const [files, setFiles] = useState([]);
    const [existingFileUrls, setExistingFileUrls] = useState([]);
    const [keywords, setKeywords] = useState([]);
    const [keywordInput, setKeywordInput] = useState("");
    const [socialLinks, setSocialLinks] = useState(() => normalizeSocialLinks());
    const [toast, setToast] = useState({ show: false, message: "", type: "" });

    const fileInputRef = useRef(null);
    const titleColorPickerRef = useRef(null);
    const categoryColorPickerRef = useRef(null);

    useEffect(() => {
        if (blog) {
            setTitle(blog.title || "");
            setSubTitle(blog.subTitle || "");
            setCategory(blog.category || "");
            setTitlePosition(blog.titlePosition || "bottom");
            setShortDescription(blog.shortDescription || "");
            setDescription(blog.description || "");
            setTitleColor(blog.titleColor || "#000000");
            setCategoryColor(blog.categoryColor || "#000000");
            setFiles([]);
            const urls = blog.images
                ? (Array.isArray(blog.images) ? blog.images : [blog.images])
                : [];
            setExistingFileUrls(urls);
            setKeywords(blog.keywords || []);
            setSocialLinks(normalizeSocialLinks(blog));
        } else {
            setTitle("");
            setSubTitle("");
            setCategory("");
            setTitlePosition("bottom");
            setShortDescription("");
            setDescription("");
            setTitleColor("#000000");
            setCategoryColor("#000000");
            setFiles([]);
            setExistingFileUrls([]);
            setKeywords([]);
            setSocialLinks(normalizeSocialLinks());
        }
        setKeywordInput("");
        setShowTitleColorPicker(false);
        setShowCategoryColorPicker(false);
    }, [blog]);

    const fileUrls = useMemo(() => {
        return files.map((file) => URL.createObjectURL(file));
    }, [files]);

    useEffect(() => {
        return () => {
            fileUrls.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [fileUrls]);

    // Close color pickers when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            const isOutsideTitlePicker = titleColorPickerRef.current &&
                !titleColorPickerRef.current.contains(event.target);
            const isOutsideCategoryPicker = categoryColorPickerRef.current &&
                !categoryColorPickerRef.current.contains(event.target);

            if (showTitleColorPicker && isOutsideTitlePicker) {
                setShowTitleColorPicker(false);
            }
            if (showCategoryColorPicker && isOutsideCategoryPicker) {
                setShowCategoryColorPicker(false);
            }
        };

        if (showTitleColorPicker || showCategoryColorPicker) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showTitleColorPicker, showCategoryColorPicker]);

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files || []);
        if (selectedFiles.length > 0) {
            setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleRemoveFile = (index) => {
        setFiles((prevFiles) => {
            const newFiles = [...prevFiles];
            newFiles.splice(index, 1);
            return newFiles;
        });
    };

    const handleRemoveExistingFile = (index) => {
        setExistingFileUrls((prevUrls) => {
            const newUrls = [...prevUrls];
            newUrls.splice(index, 1);
            return newUrls;
        });
    };

    const handleUploadClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleKeywordKeyDown = (e) => {
        if (e.key === "Enter" && keywordInput.trim()) {
            e.preventDefault();
            const trimmedKeyword = keywordInput.trim();
            if (!keywords.includes(trimmedKeyword)) {
                setKeywords([...keywords, trimmedKeyword]);
            }
            setKeywordInput("");
        }
    };

    const handleRemoveKeyword = (keywordToRemove) => {
        setKeywords(keywords.filter((kw) => kw !== keywordToRemove));
    };

    const handleSocialLinkChange = (platform, value) => {
        setSocialLinks(prev => ({
            ...prev,
            [platform]: value
        }));
    };

    const handleSave = async () => {
        if (addAndEditLoader) return;

        if (files.length === 0 && existingFileUrls.length === 0) {
            setToast({
                show: true,
                message: "Please add at least one image or video file.",
                type: "error"
            });
            return;
        }

        try {
            setAddAndEditLoader(true);
            const formData = new FormData();
            formData.append("title", title);
            formData.append("subTitle", subTitle);
            formData.append("category", category);
            formData.append("titlePosition", titlePosition);
            formData.append("shortDescription", shortDescription);
            formData.append("description", description);
            formData.append("titleColor", titleColor);
            formData.append("categoryColor", categoryColor);
            formData.append("keywords", JSON.stringify(keywords));
            formData.append("socialLinks", JSON.stringify(socialLinks));

            files.forEach((file) => {
                formData.append("files", file);
            });

            if (existingFileUrls.length > 0) {
                formData.append("existingImages", JSON.stringify(existingFileUrls));
            }

            const res = await fetch(
                `${import.meta.env.VITE_API_URL}/api/admin-user/blogs${blog ? `?id=${blog._id}` : ""}`,
                {
                    method: blog ? "PUT" : "POST",
                    body: formData,
                }
            );

            if (res.ok) {
                onSaved();
                onClose();
            }
        } catch (error) {
            console.error(error);
        } finally {
            setAddAndEditLoader(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg w-full max-w-2xl max-h-[96vh] overflow-y-auto p-6">
                <h2 className="text-xl font-bold mb-4">
                    {blog ? "Edit Blog" : "Add Blog"}
                </h2>

                {/* Title */}
                <label className="font-medium mb-1">Title</label>
                <input
                    type="text"
                    placeholder="Title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full border focus:border-primary-900 focus-within:border-primary-900 focus-within:ring-1 focus-within:ring-primary-900 rounded px-3 py-2 mb-4"
                />

                {/* Subtitle */}
                <label className="font-medium mb-1">Subtitle (optional)</label>
                <input
                    type="text"
                    placeholder="Subtitle"
                    value={subTitle}
                    onChange={(e) => setSubTitle(e.target.value)}
                    className="w-full border focus:border-primary-900 focus-within:border-primary-900 focus-within:ring-1 focus-within:ring-primary-900 rounded px-3 py-2 mb-4"
                />

                {/* Category */}
                <label className="font-medium mb-1">Category (optional)</label>
                <input
                    type="text"
                    placeholder="Category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full border focus:border-primary-900 focus-within:border-primary-900 focus-within:ring-1 focus-within:ring-primary-900 rounded px-3 py-2 mb-4"
                />

                {/* Title Color */}
                <div className="flex items-center gap-2">
                    <div className="w-full">
                        <label className="font-medium mb-1">Title Color</label>
                        <div className="relative mb-4 w-full" ref={titleColorPickerRef}>
                            <div className="flex items-center gap-3 w-full">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowTitleColorPicker(!showTitleColorPicker);
                                        setShowCategoryColorPicker(false);
                                    }}
                                    className={`flex items-center gap-2 px-3 py-2  border border-gray-400 rounded w-full transition-colors ${showTitleColorPicker ? 'border-primary-900 ring-1 ring-primary-900' : ''}`}
                                >
                                    <div
                                        className="w-8 h-8 rounded border-2 border-gray-300 shadow-sm"
                                        style={{ backgroundColor: titleColor }}
                                    />
                                    <span className="text-sm text-gray-700 font-mono">{titleColor}</span>
                                </button>
                            </div>
                            {showTitleColorPicker && (
                                <div className="absolute z-10 mt-2 p-4 bg-white rounded-lg shadow-xl border border-primary-900">
                                    <HexColorPicker
                                        className="w-full border-primary-900"
                                        color={titleColor}
                                        onChange={setTitleColor}
                                    />
                                    <div className="mt-3 flex w-full items-center gap-2">
                                        <input
                                            type="text"
                                            value={titleColor}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                                                    setTitleColor(value);
                                                }
                                            }}
                                            className="flex-1 px-2 py-1 border rounded text-sm font-mono"
                                            placeholder="#000000"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Category Color */}
                    <div className="w-full">
                        <label className="font-medium mb-1">Category Color</label>
                        <div className="relative mb-4 w-full" ref={categoryColorPickerRef}>
                            <div className="flex items-center gap-3 w-full">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCategoryColorPicker(!showCategoryColorPicker);
                                        setShowTitleColorPicker(false);
                                    }}
                                    className={`flex items-center gap-2 px-3 py-2 border border-gray-400 rounded w-full transition-colors ${showCategoryColorPicker ? 'border-primary-900 ring-1 ring-primary-900' : ''}`}
                                >
                                    <div
                                        className="w-8 h-8 rounded border-2 border-gray-300 shadow-sm"
                                        style={{ backgroundColor: categoryColor }}
                                    />
                                    <span className="text-sm text-gray-700 font-mono">{categoryColor}</span>
                                </button>
                            </div>
                            {showCategoryColorPicker && (
                                <div className="absolute z-10 mt-2 p-4 bg-white rounded-lg shadow-xl border border-primary-900">
                                    <HexColorPicker
                                        className="w-full border-primary-900"
                                        color={categoryColor}
                                        onChange={setCategoryColor}
                                    />
                                    <div className="mt-3 flex w-full items-center gap-2">
                                        <input
                                            type="text"
                                            value={categoryColor}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                                                    setCategoryColor(value);
                                                }
                                            }}
                                            className="flex-1 px-2 py-1 border rounded text-sm font-mono"
                                            placeholder="#000000"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Title Position */}
                <label className="font-medium mb-1">Title Position</label>
                <select
                    value={titlePosition}
                    onChange={(e) => setTitlePosition(e.target.value)}
                    className="w-full border focus:border-primary-900 focus-within:border-primary-900 focus-within:ring-1 focus-within:ring-primary-900 rounded px-3 py-2 mb-4"
                >
                    <option value="top">Top</option>
                    <option value="bottom">Bottom</option>
                </select>

                {/* Short Description */}
                <label className="font-medium mb-1">Short Description</label>
                <ReactQuill
                    value={shortDescription}
                    onChange={setShortDescription}
                    className="mb-4"
                />

                {/* Description */}
                <label className="font-medium mb-1">Description</label>
                <ReactQuill
                    value={description}
                    onChange={setDescription}
                    className="mb-4"
                />

                {/* Keywords */}
                <label className="font-medium mb-1">Keywords</label>
                <div className="mb-4">
                    <div className={`w-full border rounded flex flex-wrap items-center gap-2 focus:border-primary-900 focus-within:border-primary-900 focus-within:ring-1 focus-within:ring-primary-900 px-2`}>
                        <div className="flex flex-wrap items-center gap-2 py-2">
                            {keywords.map((keyword, index) => (
                                <div
                                    key={index}
                                    className={`flex items-center gap-1 bg-primary-100 text-primary px-3 py-1 rounded-full text-sm`}
                                >
                                    <span>{keyword}</span>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveKeyword(keyword);
                                        }}
                                        className="hover:text-red-500 border-none flex items-center justify-center"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <input
                            type="text"
                            placeholder={keywords.length === 0 ? "Type keyword and press Enter" : ""}
                            value={keywordInput}
                            onChange={(e) => setKeywordInput(e.target.value)}
                            onKeyDown={handleKeywordKeyDown}
                            className="flex-1 min-w-[120px] outline-none border-none ring-0 bg-transparent focus:ring-0 focus:border-none focus:outline-none"
                        />
                    </div>
                </div>

                {/* Social Links */}
                <label className="font-medium mb-1">Social Links</label>
                <div className="mb-4 space-y-2">
                    <input
                        type="text"
                        placeholder="Facebook link"
                        value={socialLinks.facebook}
                        onChange={(e) => handleSocialLinkChange("facebook", e.target.value)}
                        className="w-full border focus:border-primary-900 focus-within:border-primary-900 focus-within:ring-1 focus-within:ring-primary-900 rounded px-3 py-2"
                    />
                    <input
                        type="text"
                        placeholder="Instagram link"
                        value={socialLinks.instagram}
                        onChange={(e) => handleSocialLinkChange("instagram", e.target.value)}
                        className="w-full border focus:border-primary-900 focus-within:border-primary-900 focus-within:ring-1 focus-within:ring-primary-900 rounded px-3 py-2"
                    />
                    <input
                        type="text"
                        placeholder="LinkedIn link"
                        value={socialLinks.linkedin}
                        onChange={(e) => handleSocialLinkChange("linkedin", e.target.value)}
                        className="w-full border focus:border-primary-900 focus-within:border-primary-900 focus-within:ring-1 focus-within:ring-primary-900 rounded px-3 py-2"
                    />
                    <input
                        type="text"
                        placeholder="Website link"
                        value={socialLinks.website}
                        onChange={(e) => handleSocialLinkChange("website", e.target.value)}
                        className="w-full border focus:border-primary-900 focus-within:border-primary-900 focus-within:ring-1 focus-within:ring-primary-900 rounded px-3 py-2"
                    />
                    <input
                        type="text"
                        placeholder="Twitter link"
                        value={socialLinks.twitter}
                        onChange={(e) => handleSocialLinkChange("twitter", e.target.value)}
                        className="w-full border focus:border-primary-900 focus-within:border-primary-900 focus-within:ring-1 focus-within:ring-primary-900 rounded px-3 py-2"
                    />
                </div>

                {/* Images Section */}
                <div className="my-4">
                    <label className="font-medium mb-2 block">Images</label>
                    <div className="flex flex-wrap gap-4">
                        {/* Existing Images */}
                        {existingFileUrls.map((url, index) => (
                            <div
                                key={`existing-${index}`}
                                className="relative w-40 h-32 border-2 border-gray-200 rounded-lg overflow-hidden"
                            >
                                {url.includes('mp4') || url.includes('video') ? (
                                    <video
                                        src={url}
                                        className="w-full h-full object-cover"
                                        controls
                                    />
                                ) : (
                                    <img
                                        src={url}
                                        alt={`Preview ${index + 1}`}
                                        className="w-full h-full object-cover"
                                    />
                                )}
                                {/* Remove Button */}
                                <button
                                    type="button"
                                    onClick={() => handleRemoveExistingFile(index)}
                                    className="absolute top-1 right-1 bg-white rounded-full p-1 shadow hover:bg-red-500 hover:text-white transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        ))}

                        {/* New Uploaded Images */}
                        {files.map((file, index) => (
                            <div
                                key={`new-${index}`}
                                className="relative w-40 h-32 border-2 border-gray-200 rounded-lg overflow-hidden"
                            >
                                {file.type?.startsWith("video/") ? (
                                    <video
                                        src={fileUrls[index]}
                                        className="w-full h-full object-cover"
                                        controls
                                    />
                                ) : (
                                    <img
                                        src={fileUrls[index]}
                                        alt={`Preview ${index + 1}`}
                                        className="w-full h-full object-cover"
                                    />
                                )}
                                {/* Remove Button */}
                                <button
                                    type="button"
                                    onClick={() => handleRemoveFile(index)}
                                    className="absolute top-1 right-1 bg-white rounded-full p-1 shadow hover:bg-red-500 hover:text-white transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        ))}

                        {/* Upload Box */}
                        <div
                            className="relative w-40 h-32 flex flex-col items-center justify-center border-2 border-dashed border-primary rounded-lg cursor-pointer hover:bg-primary/5 transition-colors"
                            onClick={handleUploadClick}
                        >
                            <div className="flex flex-col items-center text-gray-500">
                                <Upload className="w-6 h-6 mb-1 text-primary" />
                                <span className="text-sm">Upload Image</span>
                            </div>
                        </div>
                    </div>
                    {/* hidden input */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,video/*"
                        multiple
                        className="hidden"
                        onChange={handleFileChange}
                    />
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-2 mt-4">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-300 rounded"
                    >
                        Cancel
                    </button>
                    <button
                        disabled={addAndEditLoader}
                        onClick={handleSave}
                        className="px-4 py-2 bg-primary-600 text-white rounded flex items-center gap-2"
                    >
                        {addAndEditLoader && (
                            <Loader className="w-4 h-4 animate-spin" />
                        )}
                        {blog ? "Update" : "Add"}
                    </button>
                </div>
            </div>

            {toast.show && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast({ ...toast, show: false })}
                />
            )}
        </div>
    );
};

export default BlogModal;